import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { PlusIcon, UploadIcon, XIcon } from 'lucide-react';
import { Avatar, Button, ErrorNotice, Field, SelectInput, TextInput } from '@bcecloud/portal-ui';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { errorMessage, uploadMedia } from '@/lib/api';
import {
  avatarSrc,
  personName,
  updateMyProfile,
  updateUser,
  type PersonProfile,
  type UserPatch,
} from '@/lib/events';

/** Assignable roles, least to most privileged. */
const ROLES = ['client', 'contractor', 'internal', 'admin', 'superadmin'] as const;

export interface EditUserDialogProps {
  readonly user: PersonProfile;
  /**
   * 'admin' exposes the whole surface (email, associated emails, role);
   * 'self' is the safe subset a person may change about themselves.
   */
  readonly mode: 'admin' | 'self';
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}

/** The editable form state, all as strings for the inputs. */
interface Form {
  firstName: string;
  lastName: string;
  company: string;
  email: string;
  associatedEmails: string[];
  phone: string;
  jobTitle: string;
  role: string;
  street: string;
  city: string;
  state: string;
  postalCode: string;
  avatar: string;
}

/** Seeds the form from the profile. */
function toForm(user: PersonProfile): Form {
  return {
    firstName: user.firstName ?? '',
    lastName: user.lastName ?? '',
    company: user.company ?? '',
    email: user.email ?? '',
    associatedEmails: [...(user.associatedEmails ?? [])],
    phone: user.phone ?? '',
    jobTitle: user.jobTitle ?? '',
    role: user.role ?? 'contractor',
    street: user.address?.street ?? '',
    city: user.address?.city ?? '',
    state: user.address?.state ?? '',
    postalCode: user.address?.postalCode ?? '',
    avatar: user.avatar ?? '',
  };
}

/**
 * The user editor, in a modal — the modern version of the legacy Edit User
 * form: profile picture upload, names, company, contact details, role and
 * address, with associated emails managed as rows.
 *
 * In 'self' mode the authentication-affecting fields (email, associated
 * emails, role) are hidden; the server enforces the same boundary regardless.
 *
 * @param props - The user, the mode, and open/close/saved handlers.
 * @returns The edit dialog.
 */
export function EditUserDialog({
  user,
  mode,
  open,
  onClose,
  onSaved,
}: EditUserDialogProps): React.ReactElement {
  const [form, setForm] = useState<Form>(() => toForm(user));
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const admin = mode === 'admin';

  // Re-seed whenever the dialog opens for a (possibly refreshed) user.
  useEffect(() => {
    if (open) setForm(toForm(user));
  }, [open, user]);

  const set = <K extends keyof Form>(key: K, value: Form[K]): void =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const save = useMutation({
    mutationFn: () => {
      const address = {
        street: form.street.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        postalCode: form.postalCode.trim(),
      };
      const base: UserPatch = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        company: form.company.trim(),
        phone: form.phone.trim(),
        jobTitle: form.jobTitle.trim(),
        address,
        avatar: form.avatar.trim() || null,
      };
      if (!admin) return updateMyProfile(base);
      return updateUser(user._id, {
        ...base,
        email: form.email.trim(),
        role: form.role,
        associatedEmails: form.associatedEmails.map((e) => e.trim()).filter(Boolean),
      });
    },
    onSuccess: onSaved,
  });

  async function onAvatarFile(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setUploadError(null);
    setUploading(true);
    try {
      const media = await uploadMedia(file, { kind: 'avatar' });
      set('avatar', media.url);
    } catch (err) {
      setUploadError(errorMessage(err));
    } finally {
      setUploading(false);
    }
  }

  const displayName = personName(user);

  return (
    <Dialog open={open} onOpenChange={(next) => (!next && !save.isPending ? onClose() : undefined)}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-3xl">
        <DialogTitle>{admin ? `Edit ${displayName}` : 'Edit your profile'}</DialogTitle>

        <form
          className="mt-3 space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate();
          }}
        >
          {/* ── Profile picture ─────────────────────────────────────────── */}
          <div className="flex items-center gap-4">
            <Avatar name={displayName} size={64} imageUrl={avatarSrc({ _id: user._id, avatar: form.avatar })} />
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-2">
                <label className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-md border border-admin-line px-3 text-sm font-semibold hover:bg-admin-subtle">
                  <UploadIcon aria-hidden className="size-4" />
                  {uploading ? 'Uploading…' : form.avatar ? 'Replace photo' : 'Upload photo'}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(event) => void onAvatarFile(event)}
                    className="sr-only"
                  />
                </label>
                {form.avatar ? (
                  <Button variant="ghost" onClick={() => set('avatar', '')} disabled={uploading}>
                    Remove
                  </Button>
                ) : null}
              </div>
              {uploadError ? (
                <p className="text-portal-micro font-semibold text-red-600">{uploadError}</p>
              ) : (
                <p className="text-portal-micro text-portal-muted">PNG or JPEG, up to 5MB.</p>
              )}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="First name" htmlFor="eu-first">
              <TextInput id="eu-first" required value={form.firstName} onChange={(e) => set('firstName', e.target.value)} />
            </Field>
            <Field label="Last name" htmlFor="eu-last">
              <TextInput id="eu-last" required value={form.lastName} onChange={(e) => set('lastName', e.target.value)} />
            </Field>
          </div>

          <Field label="Company" htmlFor="eu-company">
            <TextInput id="eu-company" value={form.company} onChange={(e) => set('company', e.target.value)} />
          </Field>

          {admin ? (
            <>
              <Field label="Email" htmlFor="eu-email">
                <TextInput id="eu-email" type="email" required value={form.email} onChange={(e) => set('email', e.target.value)} />
              </Field>

              <div>
                <span className="mb-1.5 block text-portal-tiny font-bold">Associated emails</span>
                <div className="space-y-2">
                  {form.associatedEmails.map((email, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <TextInput
                        type="email"
                        aria-label={`Associated email ${index + 1}`}
                        value={email}
                        onChange={(e) =>
                          set(
                            'associatedEmails',
                            form.associatedEmails.map((v, i) => (i === index ? e.target.value : v)),
                          )
                        }
                      />
                      <button
                        type="button"
                        aria-label="Remove associated email"
                        onClick={() =>
                          set('associatedEmails', form.associatedEmails.filter((_, i) => i !== index))
                        }
                        className="grid h-[43px] w-9 shrink-0 cursor-pointer place-items-center self-center rounded-md text-portal-muted hover:bg-red-50 hover:text-red-600"
                      >
                        <XIcon aria-hidden className="size-4" />
                      </button>
                    </div>
                  ))}
                  <Button
                    variant="secondary"
                    onClick={() => set('associatedEmails', [...form.associatedEmails, ''])}
                  >
                    <PlusIcon aria-hidden className="size-4" /> Add email
                  </Button>
                </div>
                <p className="mt-1 text-portal-micro text-portal-muted">
                  Any of these addresses can be matched to this person's account.
                </p>
              </div>
            </>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Phone" htmlFor="eu-phone">
              <TextInput id="eu-phone" value={form.phone} onChange={(e) => set('phone', e.target.value)} />
            </Field>
            <Field label="Job title" htmlFor="eu-job-title" hint="Signs the emails they send from the cloud.">
              <TextInput id="eu-job-title" value={form.jobTitle} placeholder="Senior Program Manager" onChange={(e) => set('jobTitle', e.target.value)} />
            </Field>
            {admin ? (
              <Field label="Role" htmlFor="eu-role">
                <SelectInput id="eu-role" value={form.role} onChange={(e) => set('role', e.target.value)}>
                  {ROLES.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </SelectInput>
              </Field>
            ) : null}
          </div>

          <fieldset>
            <legend className="mb-1.5 text-portal-tiny font-bold">Address</legend>
            <div className="space-y-3">
              <Field label="Street" htmlFor="eu-street">
                <TextInput id="eu-street" placeholder="123 Peachtree St NE" value={form.street} onChange={(e) => set('street', e.target.value)} />
              </Field>
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="City" htmlFor="eu-city">
                  <TextInput id="eu-city" placeholder="Atlanta" value={form.city} onChange={(e) => set('city', e.target.value)} />
                </Field>
                <Field label="State" htmlFor="eu-state">
                  <TextInput id="eu-state" placeholder="GA" value={form.state} onChange={(e) => set('state', e.target.value)} />
                </Field>
                <Field label="Postal code" htmlFor="eu-postal">
                  <TextInput id="eu-postal" placeholder="30303" value={form.postalCode} onChange={(e) => set('postalCode', e.target.value)} />
                </Field>
              </div>
            </div>
          </fieldset>

          {save.isError ? (
            <ErrorNotice inline title="Could not save" message={errorMessage(save.error)} />
          ) : null}

          <div className="flex items-center justify-end gap-3 pt-1">
            <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending || uploading}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
